import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { movimientos, cuentas, categorias } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '../components/ui/sheet';
import { toast } from 'sonner';
import { 
  Upload, 
  Search, 
  Filter, 
  Download, 
  Receipt,
  ArrowUpRight,
  ArrowDownRight,
  Loader2,
  FileSpreadsheet,
  CheckCircle,
  Trash2,
  Edit2,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Save,
  Plus,
  History,
  FileCheck,
  AlertCircle,
  XCircle,
  Eye,
  Calendar,
  X,
  FilterX,
  Users,
  Tags,
  SlidersHorizontal,
  ChevronLeft,
  ChevronRight,
  TrendingUp,
  TrendingDown
} from 'lucide-react';
import { Checkbox } from '../components/ui/checkbox';

const formatCurrency = (value) => {
  return new Intl.NumberFormat('es-MX', {
    style: 'currency',
    currency: 'MXN',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value);
};

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  const date = new Date(dateStr + 'T00:00:00');
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

export default function Movimientos() {
  const { isAdmin } = useAuth();
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [movimientosList, setMovimientosList] = useState([]);
  const [cuentasList, setCuentasList] = useState([]);
  const [categoriasList, setCategoriasList] = useState([]);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editingMov, setEditingMov] = useState(null);
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const fileInputRef = useRef(null);

  // Selection state
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  // Upload validation states
  const [uploadStep, setUploadStep] = useState('select'); // 'select', 'validation', 'result'
  const [validationData, setValidationData] = useState(null);
  const [uploadResult, setUploadResult] = useState(null);
  const [confirming, setConfirming] = useState(false);
  
  // Upload history states
  const [historialDialogOpen, setHistorialDialogOpen] = useState(false);
  const [historialCargas, setHistorialCargas] = useState([]);
  const [loadingHistorial, setLoadingHistorial] = useState(false);
  const [deletingCarga, setDeletingCarga] = useState(null);
  const [deleteHistorialDialogOpen, setDeleteHistorialDialogOpen] = useState(false);
  const [cargaToDelete, setCargaToDelete] = useState(null);
  
  // Delete ALL movements states
  const [deleteAllDialogOpen, setDeleteAllDialogOpen] = useState(false);
  const [deleteAllConfirmText, setDeleteAllConfirmText] = useState('');
  const [deletingAll, setDeletingAll] = useState(false);

  // New movement form
  const [newMov, setNewMov] = useState({
    fecha: new Date().toISOString().split('T')[0],
    concepto: '',
    cliente_proveedor: '',
    tipo: 'egreso',
    categoria_id: '',
    cuenta_id: '',
    monto: ''
  });

  // Sorting
  const [sortConfig, setSortConfig] = useState({ key: 'fecha', direction: 'desc' });

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [pageSize, setPageSize] = useState(100);
  const [globalTotals, setGlobalTotals] = useState({ ingresos: 0, egresos: 0 });

  // Filters
  const [filters, setFilters] = useState({
    cuenta_id: '',
    tipo: '',
    categoria_id: '',
    cliente_proveedor: '',  // New filter
    concepto: '',
    fecha_inicio: '',
    fecha_fin: '',
    consolidado: false
  });

  // Exclusion filters
  const [excludedCategorias, setExcludedCategorias] = useState([]);
  const [excludedClientes, setExcludedClientes] = useState([]);
  const [uniqueClientes, setUniqueClientes] = useState([]);
  const [uniqueClientesIngresos, setUniqueClientesIngresos] = useState([]);
  const [uniqueClientesEgresos, setUniqueClientesEgresos] = useState([]);
  const [showExclusionFilters, setShowExclusionFilters] = useState(false);
  
  // Mobile filter sheet
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    setCurrentPage(1); // Reset to page 1 when filters change
    loadMovimientos();
  }, [filters, excludedCategorias, excludedClientes]);

  useEffect(() => {
    loadMovimientos();
  }, [currentPage, pageSize]);

  const loadInitialData = async () => {
    try {
      const [cuentasRes, categoriasRes] = await Promise.all([
        cuentas.getAll(),
        categorias.getAll()
      ]);
      setCuentasList(cuentasRes.data);
      // Sort categories alphabetically by name
      const sortedCategorias = [...categoriasRes.data].sort((a, b) => 
        (a.nombre || '').localeCompare(b.nombre || '', 'es', { sensitivity: 'base' })
      );
      setCategoriasList(sortedCategorias);
    } catch (error) {
      console.error('Error loading initial data:', error);
    }
  };

  const loadMovimientos = async () => {
    setLoading(true);
    try {
      const params = {
        limit: pageSize,
        skip: (currentPage - 1) * pageSize
      };
      if (filters.cuenta_id && filters.cuenta_id !== 'all') params.cuenta_id = filters.cuenta_id;
      if (filters.tipo && filters.tipo !== 'all') params.tipo = filters.tipo;
      if (filters.categoria_id && filters.categoria_id !== 'all') params.categoria_id = filters.categoria_id;
      if (filters.cliente_proveedor && filters.cliente_proveedor !== 'all') params.cliente_proveedor = filters.cliente_proveedor;
      if (filters.concepto) params.concepto = filters.concepto;
      if (filters.fecha_inicio) params.fecha_inicio = filters.fecha_inicio;
      if (filters.fecha_fin) params.fecha_fin = filters.fecha_fin;
      if (!filters.cuenta_id || filters.cuenta_id === 'all') params.consolidado = true;
      
      // Add exclusion filters
      if (excludedCategorias.length > 0) {
        params.excluded_categorias = excludedCategorias.join(',');
      }
      if (excludedClientes.length > 0) {
        params.excluded_clientes = excludedClientes.join(',');
      }

      // Get both data and count in parallel
      const [response, countResponse] = await Promise.all([
        movimientos.getAll(params),
        movimientos.getCount ? movimientos.getCount(params) : { data: { count: 0, total_ingresos: 0, total_egresos: 0 } }
      ]);
      
      setMovimientosList(response.data);
      if (countResponse?.data?.count !== undefined) {
        setTotalCount(countResponse.data.count);
        setGlobalTotals({
          ingresos: countResponse.data.total_ingresos || 0,
          egresos: countResponse.data.total_egresos || 0
        });
      }
      
      // Extract unique clientes/proveedores from all movements
      if (response.data && response.data.length > 0) {
        const clientesSet = new Set();
        response.data.forEach(mov => {
          if (mov.cliente_proveedor) {
            clientesSet.add(mov.cliente_proveedor);
          }
        });
        // Merge with existing unique clientes
        setUniqueClientes(prev => {
          const merged = new Set([...prev, ...clientesSet]);
          return Array.from(merged).sort();
        });
      }
    } catch (error) {
      console.error('Error loading movements:', error);
      toast.error('Error al cargar movimientos');
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!filters.cuenta_id || filters.cuenta_id === 'all') {
      toast.error('Selecciona una cuenta antes de subir el archivo');
      return;
    }

    setUploading(true);
    setUploadStep('validation');
    
    try {
      // Step 1: Analyze file without saving
      const response = await movimientos.analyze(file, filters.cuenta_id);
      setValidationData(response.data);
      
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al analizar archivo');
      setUploadStep('select');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleConfirmUpload = async (forzarTodos = false) => {
    if (!validationData?.upload_id) return;
    
    setConfirming(true);
    try {
      const response = await movimientos.confirm(validationData.upload_id, forzarTodos);
      setUploadResult(response.data);
      setUploadStep('result');
      toast.success(`✅ ${response.data.guardados?.registros || 0} movimientos guardados`);
      loadMovimientos();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar movimientos');
    } finally {
      setConfirming(false);
    }
  };

  const handleCancelUpload = async () => {
    if (validationData?.upload_id) {
      try {
        await movimientos.cancelUpload(validationData.upload_id);
      } catch (e) {
        // Ignore errors on cancel
      }
    }
    resetUploadDialog();
  };

  const resetUploadDialog = () => {
    setUploadStep('select');
    setValidationData(null);
    setUploadResult(null);
    setUploadDialogOpen(false);
  };

  const loadHistorial = async () => {
    setLoadingHistorial(true);
    try {
      const response = await movimientos.getHistorial(50);
      setHistorialCargas(response.data);
    } catch (error) {
      toast.error('Error al cargar historial');
    } finally {
      setLoadingHistorial(false);
    }
  };

  const handleDeleteCarga = (carga) => {
    setCargaToDelete(carga);
    setDeleteHistorialDialogOpen(true);
  };

  const confirmDeleteCarga = async () => {
    if (!cargaToDelete) return;
    
    setDeletingCarga(cargaToDelete.id);
    try {
      const response = await movimientos.deleteHistorial(cargaToDelete.id);
      toast.success(`✅ Carga eliminada: ${response.data.movimientos_eliminados} movimientos borrados`);
      loadHistorial();
      loadMovimientos(); // Refresh movements list
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar carga');
    } finally {
      setDeletingCarga(null);
      setDeleteHistorialDialogOpen(false);
      setCargaToDelete(null);
    }
  };

  const handleDeleteAll = async () => {
    if (deleteAllConfirmText !== 'ELIMINAR TODO') {
      toast.error('Debes escribir exactamente "ELIMINAR TODO" para confirmar');
      return;
    }
    
    setDeletingAll(true);
    try {
      const response = await movimientos.deleteAll('ELIMINAR TODO');
      toast.success(`🗑️ ${response.data.movimientos_eliminados} movimientos eliminados`);
      setDeleteAllDialogOpen(false);
      setDeleteAllConfirmText('');
      loadMovimientos();
      loadHistorial();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar movimientos');
    } finally {
      setDeletingAll(false);
    }
  };

  // Load unique clientes/proveedores for exclusion filter
  const loadUniqueClientes = async () => {
    try {
      const params = {};
      if (filters.cuenta_id && filters.cuenta_id !== 'all') {
        params.cuenta_id = filters.cuenta_id;
      } else {
        params.consolidado = true;
      }
      const response = await movimientos.getClientesUnicos(params);
      if (response.data) {
        setUniqueClientes(response.data.clientes || []);
        setUniqueClientesIngresos(response.data.clientes_ingresos || []);
        setUniqueClientesEgresos(response.data.clientes_egresos || []);
      }
    } catch (error) {
      console.error('Error loading unique clientes:', error);
    }
  };

  // Load unique clientes when component mounts or cuenta changes
  useEffect(() => {
    loadUniqueClientes();
  }, [filters.cuenta_id]);

  const handleCategoryChange = async (movimientoId, categoriaId) => {
    try {
      await movimientos.update(movimientoId, { categoria_id: categoriaId });
      
      // Update the local state without reloading the entire list
      const categoria = categoriasList.find(c => c.id === categoriaId);
      setMovimientosList(prev => prev.map(mov => 
        mov.id === movimientoId 
          ? { 
              ...mov, 
              categoria_id: categoriaId,
              categoria_nombre: categoria?.nombre || null
            }
          : mov
      ));
      
      toast.success('Categoría actualizada');
    } catch (error) {
      toast.error('Error al actualizar categoría');
    }
  };

  // Selection handlers
  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedIds(new Set(sortedMovimientos.map(m => m.id)));
    } else {
      setSelectedIds(new Set());
    }
  };

  const handleSelectOne = (id, checked) => {
    const newSelected = new Set(selectedIds);
    if (checked) {
      newSelected.add(id);
    } else {
      newSelected.delete(id);
    }
    setSelectedIds(newSelected);
  };

  const handleDeleteSelected = async () => {
    if (selectedIds.size === 0) return;
    
    setDeleting(true);
    try {
      // Delete each selected movement
      const deletePromises = Array.from(selectedIds).map(id => 
        movimientos.delete(id)
      );
      await Promise.all(deletePromises);
      
      toast.success(`${selectedIds.size} movimiento(s) eliminado(s)`);
      setSelectedIds(new Set());
      setDeleteDialogOpen(false);
      loadMovimientos();
    } catch (error) {
      toast.error('Error al eliminar movimientos');
    } finally {
      setDeleting(false);
    }
  };

  // Sorting function
  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return <ArrowUpDown className="w-3 h-3 ml-1 opacity-30" />;
    return sortConfig.direction === 'asc' 
      ? <ArrowUp className="w-3 h-3 ml-1 text-primary" />
      : <ArrowDown className="w-3 h-3 ml-1 text-primary" />;
  };

  // Sorted data
  const sortedMovimientos = useMemo(() => {
    const sorted = [...movimientosList];
    sorted.sort((a, b) => {
      let aVal = a[sortConfig.key];
      let bVal = b[sortConfig.key];
      
      // Handle special cases
      if (sortConfig.key === 'monto') {
        aVal = parseFloat(aVal) || 0;
        bVal = parseFloat(bVal) || 0;
      } else if (sortConfig.key === 'fecha') {
        aVal = new Date(aVal || '1970-01-01');
        bVal = new Date(bVal || '1970-01-01');
      } else {
        aVal = (aVal || '').toString().toLowerCase();
        bVal = (bVal || '').toString().toLowerCase();
      }
      
      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [movimientosList, sortConfig]);

  // Delete movement
  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar este movimiento?')) return;
    try {
      await movimientos.delete(id);
      toast.success('Movimiento eliminado');
      loadMovimientos();
    } catch (error) {
      toast.error('Error al eliminar movimiento');
    }
  };

  // Edit movement
  const openEditDialog = (mov) => {
    setEditingMov({
      id: mov.id,
      fecha: mov.fecha,
      concepto: mov.concepto,
      cliente_proveedor: mov.cliente_proveedor || '',
      tipo: mov.tipo,
      categoria_id: mov.categoria_id || '',
      monto: mov.monto
    });
    setEditDialogOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!editingMov) return;
    setSaving(true);
    try {
      await movimientos.update(editingMov.id, {
        fecha: editingMov.fecha,
        concepto: editingMov.concepto,
        cliente_proveedor: editingMov.cliente_proveedor,
        tipo: editingMov.tipo,
        categoria_id: editingMov.categoria_id || null,
        monto: parseFloat(editingMov.monto)
      });
      toast.success('Movimiento actualizado');
      setEditDialogOpen(false);
      setEditingMov(null);
      loadMovimientos();
    } catch (error) {
      toast.error('Error al actualizar movimiento');
    } finally {
      setSaving(false);
    }
  };

  // Create new movement
  const resetNewMov = () => {
    setNewMov({
      fecha: new Date().toISOString().split('T')[0],
      concepto: '',
      cliente_proveedor: '',
      tipo: 'egreso',
      categoria_id: '',
      cuenta_id: cuentasList[0]?.id || '',
      monto: ''
    });
  };

  const handleCreateMov = async () => {
    if (!newMov.concepto || !newMov.cuenta_id || !newMov.monto) {
      toast.error('Completa los campos requeridos');
      return;
    }
    setSaving(true);
    try {
      await movimientos.create({
        fecha: newMov.fecha,
        concepto: newMov.concepto,
        cliente_proveedor: newMov.cliente_proveedor || null,
        tipo: newMov.tipo,
        categoria_id: newMov.tipo === 'egreso' ? (newMov.categoria_id || null) : null,
        cuenta_id: newMov.cuenta_id,
        monto: parseFloat(newMov.monto)
      });
      toast.success('Movimiento creado');
      setCreateDialogOpen(false);
      resetNewMov();
      loadMovimientos();
    } catch (error) {
      toast.error('Error al crear movimiento: ' + (error.response?.data?.detail || ''));
    } finally {
      setSaving(false);
    }
  };

  // Download Excel
  const handleDownloadExcel = async () => {
    setDownloading(true);
    try {
      const response = await movimientos.downloadExcel(filters);
      const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `movimientos_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Excel descargado');
    } catch (error) {
      toast.error('Error al descargar Excel');
    } finally {
      setDownloading(false);
    }
  };

  // Download Template
  const handleDownloadPlantilla = async () => {
    try {
      const response = await movimientos.downloadPlantilla();
      const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'Plantilla_Movimientos_FinScope.xlsx';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Plantilla descargada');
    } catch (error) {
      toast.error('Error al descargar plantilla');
    }
  };

  // Use global totals instead of page-level totals
  const totalIngresos = globalTotals.ingresos;
  const totalEgresos = globalTotals.egresos;

  return (
    <div data-testid="movimientos-page">
      {/* Header */}
      <div className="page-header">
        <div className="page-header-mobile">
          <div>
            <h1 style={{ fontFamily: 'Outfit, sans-serif' }}>
              Movimientos
            </h1>
            <p className="hidden sm:block">
              Visualiza y gestiona todos los movimientos financieros
            </p>
          </div>
          <div className="page-header-actions">
            {/* Delete Selected Button - Only show when items are selected */}
            {isAdmin && selectedIds.size > 0 && (
              <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
                <DialogTrigger asChild>
                  <Button 
                    variant="destructive" 
                    size="sm"
                    data-testid="delete-selected-btn"
                  >
                    <Trash2 className="w-4 h-4 sm:mr-2" />
                    <span className="hidden sm:inline">Eliminar ({selectedIds.size})</span>
                    <span className="sm:hidden">{selectedIds.size}</span>
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle className="text-red-600">
                      Confirmar Eliminación
                    </DialogTitle>
                  </DialogHeader>
                  <div className="py-4">
                    <p className="text-sm text-muted-foreground">
                      ¿Estás seguro de que deseas eliminar <strong>{selectedIds.size}</strong> movimiento(s)?
                    </p>
                    <p className="text-sm text-red-500 mt-2">
                      Esta acción no se puede deshacer.
                    </p>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
                      Cancelar
                    </Button>
                    <Button 
                      variant="destructive" 
                      onClick={handleDeleteSelected}
                      disabled={deleting}
                      data-testid="confirm-delete-btn"
                    >
                      {deleting ? (
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      ) : (
                        <Trash2 className="w-4 h-4 mr-2" />
                      )}
                      Eliminar
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            )}

            {/* Download Excel Button */}
            <Button 
              variant="outline" 
              size="sm"
              onClick={handleDownloadExcel}
              disabled={downloading}
              data-testid="download-excel-btn"
            >
              {downloading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Download className="w-4 h-4 sm:mr-2" />
              )}
              <span className="hidden sm:inline">Descargar</span>
            </Button>
            
            {isAdmin && (
              <>
                {/* Upload Excel Dialog with Validation */}
                <Dialog open={uploadDialogOpen} onOpenChange={(open) => {
                  if (!open) resetUploadDialog();
                  else setUploadDialogOpen(true);
                }}>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <DialogTrigger asChild>
                          <Button variant="outline" size="sm" data-testid="upload-btn">
                            <Upload className="w-4 h-4 sm:mr-2" />
                            <span className="hidden sm:inline">Cargar Excel</span>
                          </Button>
                        </DialogTrigger>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Carga aquí el archivo con los movimientos financieros del periodo</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                  <DialogContent className={uploadStep === 'validation' ? "sm:max-w-4xl max-h-[90vh] overflow-y-auto" : "sm:max-w-md"}>
                    <DialogHeader>
                      <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>
                        {uploadStep === 'select' && 'Cargar Archivo Excel'}
                        {uploadStep === 'validation' && '📋 Validación de Datos'}
                        {uploadStep === 'result' && '✅ Resultado de Carga'}
                      </DialogTitle>
                    </DialogHeader>

                    {/* Step 1: File Selection */}
                    {uploadStep === 'select' && (
                      <div className="space-y-4 py-4">
                        <div className="space-y-2">
                          <Label>Cuenta destino</Label>
                          <Select 
                            value={filters.cuenta_id} 
                            onValueChange={(v) => setFilters({ ...filters, cuenta_id: v })}
                          >
                            <SelectTrigger data-testid="upload-cuenta-select">
                              <SelectValue placeholder="Seleccionar cuenta" />
                            </SelectTrigger>
                            <SelectContent>
                              {cuentasList.map((cuenta) => (
                                <SelectItem key={cuenta.id} value={cuenta.id}>
                                  {cuenta.nombre}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>

                        <div 
                          className="upload-zone"
                          onClick={() => fileInputRef.current?.click()}
                        >
                          <input
                            ref={fileInputRef}
                            type="file"
                            accept=".xlsx,.xls,.csv"
                            onChange={handleFileUpload}
                            className="hidden"
                            data-testid="file-input"
                          />
                          {uploading ? (
                            <div className="flex flex-col items-center">
                              <Loader2 className="w-12 h-12 text-primary animate-spin mb-4" />
                              <p className="text-muted-foreground">Analizando archivo...</p>
                            </div>
                          ) : (
                            <div className="flex flex-col items-center">
                              <FileSpreadsheet className="w-12 h-12 text-muted-foreground mb-4" />
                              <p className="font-medium">Arrastra tu archivo aquí</p>
                              <p className="text-sm text-muted-foreground">o haz clic para seleccionar</p>
                              <p className="text-xs text-muted-foreground mt-2">
                                Formatos: .xlsx, .xls, .csv
                              </p>
                            </div>
                          )}
                        </div>

                        {/* Download Template Button */}
                        <div className="flex justify-center">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={handleDownloadPlantilla}
                            className="gap-2"
                            data-testid="download-template-btn"
                          >
                            <Download className="w-4 h-4" />
                            Descargar Plantilla
                          </Button>
                        </div>

                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm">
                          <p className="font-medium mb-2 text-blue-800">📋 Usa nuestra plantilla oficial:</p>
                          <ul className="text-blue-700 space-y-1 text-xs">
                            <li>• Descarga la plantilla con el botón de arriba</li>
                            <li>• Llena tus movimientos siguiendo el formato</li>
                            <li>• DEPOSITOS = Ingresos | RETIROS = Egresos</li>
                            <li>• Elimina las filas de ejemplo antes de cargar</li>
                          </ul>
                        </div>
                      </div>
                    )}

                    {/* Step 2: Validation Preview */}
                    {uploadStep === 'validation' && validationData && (
                      <div className="space-y-4 py-4">
                        {/* File Summary */}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                          <Card className="bg-slate-50">
                            <CardContent className="p-4 text-center">
                              <FileSpreadsheet className="w-8 h-8 mx-auto mb-2 text-blue-600" />
                              <p className="text-xs text-muted-foreground">Archivo</p>
                              <p className="font-medium text-sm truncate" title={validationData.filename}>
                                {validationData.filename}
                              </p>
                            </CardContent>
                          </Card>
                          <Card className="bg-slate-50">
                            <CardContent className="p-4 text-center">
                              <Receipt className="w-8 h-8 mx-auto mb-2 text-purple-600" />
                              <p className="text-xs text-muted-foreground">Registros Válidos</p>
                              <p className="font-bold text-xl">{validationData.total_registros?.toLocaleString()}</p>
                            </CardContent>
                          </Card>
                          <Card className="bg-green-50 border-green-200">
                            <CardContent className="p-4 text-center">
                              <ArrowUpRight className="w-8 h-8 mx-auto mb-2 text-green-600" />
                              <p className="text-xs text-muted-foreground">Total Ingresos</p>
                              <p className="font-bold text-lg text-green-700">{formatCurrency(validationData.total_ingresos)}</p>
                            </CardContent>
                          </Card>
                          <Card className="bg-red-50 border-red-200">
                            <CardContent className="p-4 text-center">
                              <ArrowDownRight className="w-8 h-8 mx-auto mb-2 text-red-600" />
                              <p className="text-xs text-muted-foreground">Total Egresos</p>
                              <p className="font-bold text-lg text-red-700">{formatCurrency(validationData.total_egresos)}</p>
                            </CardContent>
                          </Card>
                        </div>

                        {/* Validation Info */}
                        <div className="flex items-center gap-4 text-sm">
                          <Badge variant="outline" className="gap-1">
                            <FileCheck className="w-3 h-3" />
                            Cuenta: {validationData.cuenta_nombre}
                          </Badge>
                          {validationData.filas_invalidas > 0 && (
                            <Badge variant="destructive" className="gap-1">
                              <AlertCircle className="w-3 h-3" />
                              {validationData.filas_invalidas} filas inválidas
                            </Badge>
                          )}
                          <Badge variant="secondary" className="gap-1">
                            Columnas: {validationData.columnas_detectadas?.join(', ')}
                          </Badge>
                        </div>

                        {/* Preview Table */}
                        <div className="border rounded-lg">
                          <div className="bg-slate-100 px-4 py-2 border-b">
                            <p className="font-medium text-sm flex items-center gap-2">
                              <Eye className="w-4 h-4" />
                              Vista previa (primeros 10 registros)
                            </p>
                          </div>
                          <div className="overflow-x-auto max-h-64">
                            <table className="w-full text-sm">
                              <thead className="bg-slate-50 sticky top-0">
                                <tr>
                                  <th className="px-3 py-2 text-left">#</th>
                                  <th className="px-3 py-2 text-left">Fecha</th>
                                  <th className="px-3 py-2 text-left">Concepto</th>
                                  <th className="px-3 py-2 text-left">Tipo</th>
                                  <th className="px-3 py-2 text-right">Monto</th>
                                </tr>
                              </thead>
                              <tbody>
                                {validationData.preview?.map((mov, idx) => (
                                  <tr key={idx} className="border-t hover:bg-slate-50">
                                    <td className="px-3 py-2 text-muted-foreground">{mov.row_number}</td>
                                    <td className="px-3 py-2">{formatDate(mov.fecha)}</td>
                                    <td className="px-3 py-2 max-w-xs truncate" title={mov.concepto || '(Sin concepto)'}>
                                      {mov.concepto || <span className="text-muted-foreground italic">(Sin concepto)</span>}
                                    </td>
                                    <td className="px-3 py-2">
                                      <Badge 
                                        variant={mov.tipo === 'ingreso' ? 'success' : mov.tipo === 'neutro' ? 'secondary' : 'destructive'} 
                                        className="text-xs"
                                      >
                                        {mov.tipo === 'ingreso' ? 'Ingreso' : mov.tipo === 'neutro' ? '$0' : 'Egreso'}
                                      </Badge>
                                    </td>
                                    <td className={`px-3 py-2 text-right font-mono ${mov.tipo === 'ingreso' ? 'text-green-600' : mov.tipo === 'neutro' ? 'text-gray-500' : 'text-red-600'}`}>
                                      {formatCurrency(mov.monto)}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                          {validationData.total_registros > 10 && (
                            <div className="bg-slate-50 px-4 py-2 border-t text-center text-sm text-muted-foreground">
                              ... y {validationData.total_registros - 10} registros más
                            </div>
                          )}
                        </div>

                        {/* Duplicates Warning Section - RESPONSIVE */}
                        {validationData.duplicados_detectados > 0 && (
                          <Card className="bg-orange-50 border-orange-300 border-2 overflow-hidden">
                            <CardHeader className="pb-2 px-3 sm:px-6">
                              <CardTitle className="text-sm sm:text-base flex items-center gap-2 text-orange-700 flex-wrap">
                                <AlertCircle className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
                                <span>⚠️ {validationData.duplicados_detectados} duplicados detectados</span>
                              </CardTitle>
                            </CardHeader>
                            <CardContent className="px-3 sm:px-6 pb-4">
                              <p className="text-xs sm:text-sm text-orange-700 mb-3">
                                Estos registros ya existen. ¿Qué deseas hacer?
                              </p>
                              
                              {/* Preview of duplicates - SCROLLABLE */}
                              <div className="max-h-32 sm:max-h-40 overflow-auto bg-white rounded border border-orange-200 mb-4">
                                <table className="w-full text-[10px] sm:text-xs min-w-[300px]">
                                  <thead className="bg-orange-100 sticky top-0">
                                    <tr>
                                      <th className="px-1 sm:px-2 py-1 text-left whitespace-nowrap">Fila</th>
                                      <th className="px-1 sm:px-2 py-1 text-left whitespace-nowrap">Fecha</th>
                                      <th className="px-1 sm:px-2 py-1 text-left">Concepto</th>
                                      <th className="px-1 sm:px-2 py-1 text-right whitespace-nowrap">Monto</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {validationData.duplicados_preview?.slice(0, 10).map((dup, idx) => (
                                      <tr key={idx} className="border-t border-orange-100">
                                        <td className="px-1 sm:px-2 py-1">{dup.fila}</td>
                                        <td className="px-1 sm:px-2 py-1 whitespace-nowrap">{formatDate(dup.fecha)}</td>
                                        <td className="px-1 sm:px-2 py-1 max-w-[120px] sm:max-w-[200px] truncate" title={dup.concepto}>{dup.concepto}</td>
                                        <td className={`px-1 sm:px-2 py-1 text-right whitespace-nowrap ${dup.tipo === 'ingreso' ? 'text-green-600' : 'text-red-600'}`}>
                                          {formatCurrency(dup.monto)}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                                {validationData.duplicados_detectados > 10 && (
                                  <div className="px-2 py-1 text-center text-[10px] sm:text-xs text-orange-600 bg-orange-50 sticky bottom-0">
                                    ... y {validationData.duplicados_detectados - 10} duplicados más
                                  </div>
                                )}
                              </div>

                              {/* Action Buttons - STACK ON MOBILE */}
                              <div className="flex flex-col gap-2">
                                <Button 
                                  variant="outline" 
                                  className="w-full border-orange-400 text-orange-700 hover:bg-orange-100 text-xs sm:text-sm h-10 sm:h-11"
                                  onClick={() => handleConfirmUpload(false)} 
                                  disabled={confirming}
                                >
                                  {confirming ? (
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                  ) : (
                                    <CheckCircle className="w-4 h-4 mr-2 flex-shrink-0" />
                                  )}
                                  <span className="truncate">Omitir duplicados ({validationData.nuevos_registros?.toLocaleString()} nuevos)</span>
                                </Button>
                                <Button 
                                  className="w-full bg-orange-600 hover:bg-orange-700 text-xs sm:text-sm h-10 sm:h-11"
                                  onClick={() => handleConfirmUpload(true)} 
                                  disabled={confirming}
                                >
                                  {confirming ? (
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                  ) : (
                                    <Upload className="w-4 h-4 mr-2 flex-shrink-0" />
                                  )}
                                  <span className="truncate">Cargar TODOS ({validationData.total_registros?.toLocaleString()} registros)</span>
                                </Button>
                              </div>
                            </CardContent>
                          </Card>
                        )}

                        <DialogFooter className="gap-2">
                          <Button variant="outline" onClick={handleCancelUpload} disabled={confirming}>
                            <XCircle className="w-4 h-4 mr-2" />
                            Cancelar
                          </Button>
                          {/* Only show simple confirm button if no duplicates */}
                          {!validationData.duplicados_detectados && (
                            <Button onClick={() => handleConfirmUpload(false)} disabled={confirming}>
                              {confirming ? (
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              ) : (
                                <CheckCircle className="w-4 h-4 mr-2" />
                              )}
                              Confirmar Carga ({validationData.total_registros} registros)
                            </Button>
                          )}
                        </DialogFooter>
                      </div>
                    )}

                    {/* Step 3: Result with Verification */}
                    {uploadStep === 'result' && uploadResult && (
                      <div className="space-y-4 py-4">
                        {/* Header with verification status */}
                        <div className="text-center py-2">
                          {uploadResult.verificacion?.integridad_ok ? (
                            <>
                              <CheckCircle className="w-14 h-14 mx-auto mb-3 text-green-500" />
                              <h3 className="text-xl font-bold text-green-700">✅ Carga Verificada</h3>
                              <p className="text-sm text-muted-foreground">Todos los datos coinciden correctamente</p>
                            </>
                          ) : (
                            <>
                              <AlertCircle className="w-14 h-14 mx-auto mb-3 text-orange-500" />
                              <h3 className="text-xl font-bold text-orange-700">⚠️ Verificar Datos</h3>
                              <p className="text-sm text-muted-foreground">Revisa la comparación abajo</p>
                            </>
                          )}
                        </div>

                        {/* Comparison Table: Excel vs Database */}
                        <Card className="border-2">
                          <CardHeader className="pb-2">
                            <CardTitle className="text-base flex items-center gap-2">
                              <FileCheck className="w-5 h-5" />
                              Comparación: Excel vs Base de Datos
                            </CardTitle>
                          </CardHeader>
                          <CardContent>
                            <table className="w-full text-sm">
                              <thead>
                                <tr className="border-b">
                                  <th className="text-left py-2 font-medium">Concepto</th>
                                  <th className="text-right py-2 font-medium text-blue-600">📊 Excel</th>
                                  <th className="text-right py-2 font-medium text-purple-600">💾 Guardado</th>
                                  <th className="text-center py-2 font-medium">Estado</th>
                                </tr>
                              </thead>
                              <tbody>
                                <tr className="border-b">
                                  <td className="py-2">Total Registros</td>
                                  <td className="py-2 text-right font-mono">{uploadResult.excel?.registros || 0}</td>
                                  <td className="py-2 text-right font-mono">{uploadResult.guardados?.registros || 0}</td>
                                  <td className="py-2 text-center">
                                    {uploadResult.verificacion?.registros_coinciden !== false ? (
                                      <Badge variant="success" className="text-xs">✓</Badge>
                                    ) : (
                                      <Badge variant="destructive" className="text-xs">✗</Badge>
                                    )}
                                  </td>
                                </tr>
                                <tr className="border-b">
                                  <td className="py-2">Registros Ingreso</td>
                                  <td className="py-2 text-right font-mono text-green-600">{uploadResult.excel?.registros_ingreso || 0}</td>
                                  <td className="py-2 text-right font-mono text-green-600">{uploadResult.guardados?.registros_ingreso || 0}</td>
                                  <td className="py-2 text-center">
                                    {(uploadResult.excel?.registros_ingreso || 0) === (uploadResult.guardados?.registros_ingreso || 0) ? (
                                      <Badge variant="success" className="text-xs">✓</Badge>
                                    ) : (
                                      <Badge variant="destructive" className="text-xs">✗</Badge>
                                    )}
                                  </td>
                                </tr>
                                <tr className="border-b">
                                  <td className="py-2">Registros Egreso</td>
                                  <td className="py-2 text-right font-mono text-red-600">{uploadResult.excel?.registros_egreso || 0}</td>
                                  <td className="py-2 text-right font-mono text-red-600">{uploadResult.guardados?.registros_egreso || 0}</td>
                                  <td className="py-2 text-center">
                                    {(uploadResult.excel?.registros_egreso || 0) === (uploadResult.guardados?.registros_egreso || 0) ? (
                                      <Badge variant="success" className="text-xs">✓</Badge>
                                    ) : (
                                      <Badge variant="destructive" className="text-xs">✗</Badge>
                                    )}
                                  </td>
                                </tr>
                                <tr className="border-b bg-green-50">
                                  <td className="py-2 font-medium">Suma Ingresos</td>
                                  <td className="py-2 text-right font-mono font-bold text-green-700">{formatCurrency(uploadResult.excel?.ingresos || 0)}</td>
                                  <td className="py-2 text-right font-mono font-bold text-green-700">{formatCurrency(uploadResult.guardados?.ingresos || 0)}</td>
                                  <td className="py-2 text-center">
                                    {uploadResult.verificacion?.ingresos_coinciden !== false ? (
                                      <Badge variant="success" className="text-xs">✓</Badge>
                                    ) : (
                                      <Badge variant="destructive" className="text-xs">✗</Badge>
                                    )}
                                  </td>
                                </tr>
                                <tr className="bg-red-50">
                                  <td className="py-2 font-medium">Suma Egresos</td>
                                  <td className="py-2 text-right font-mono font-bold text-red-700">{formatCurrency(uploadResult.excel?.egresos || 0)}</td>
                                  <td className="py-2 text-right font-mono font-bold text-red-700">{formatCurrency(uploadResult.guardados?.egresos || 0)}</td>
                                  <td className="py-2 text-center">
                                    {uploadResult.verificacion?.egresos_coinciden !== false ? (
                                      <Badge variant="success" className="text-xs">✓</Badge>
                                    ) : (
                                      <Badge variant="destructive" className="text-xs">✗</Badge>
                                    )}
                                  </td>
                                </tr>
                              </tbody>
                            </table>
                          </CardContent>
                        </Card>

                        {/* Duplicates info */}
                        {uploadResult.duplicados?.cantidad > 0 && (
                          <Card className="bg-orange-50 border-orange-200">
                            <CardHeader className="pb-2">
                              <CardTitle className="text-sm flex items-center gap-2 text-orange-700">
                                <AlertCircle className="w-4 h-4" />
                                {uploadResult.duplicados.cantidad} Registros Omitidos (Duplicados)
                              </CardTitle>
                            </CardHeader>
                            <CardContent className="text-xs">
                              <p className="text-orange-600 mb-2">Estos registros ya existían en la base de datos:</p>
                              <div className="max-h-32 overflow-y-auto">
                                {uploadResult.duplicados.detalle?.map((dup, idx) => (
                                  <div key={idx} className="py-1 border-b border-orange-200 last:border-0">
                                    Fila {dup.fila}: {dup.fecha} | {dup.concepto} | {formatCurrency(dup.monto)}
                                  </div>
                                ))}
                                {uploadResult.duplicados.cantidad > 10 && (
                                  <div className="py-1 text-muted-foreground">
                                    ... y {uploadResult.duplicados.cantidad - 10} más
                                  </div>
                                )}
                              </div>
                            </CardContent>
                          </Card>
                        )}

                        {/* Final Summary */}
                        <div className={`p-4 rounded-lg border-2 ${uploadResult.verificacion?.integridad_ok ? 'bg-green-50 border-green-300' : 'bg-orange-50 border-orange-300'}`}>
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="font-medium">
                                {uploadResult.verificacion?.integridad_ok 
                                  ? '✅ Todos los datos del Excel fueron guardados correctamente' 
                                  : '⚠️ Revisar diferencias en la tabla de comparación'}
                              </p>
                              <p className="text-sm text-muted-foreground">
                                Batch ID: {uploadResult.batch_id?.substring(0, 8)}...
                              </p>
                            </div>
                            <div className="text-right">
                              <p className="text-2xl font-bold">{uploadResult.guardados?.registros || 0}</p>
                              <p className="text-xs text-muted-foreground">registros guardados</p>
                            </div>
                          </div>
                        </div>

                        <DialogFooter>
                          <Button onClick={resetUploadDialog}>
                            Cerrar
                          </Button>
                        </DialogFooter>
                      </div>
                    )}
                  </DialogContent>
                </Dialog>

                {/* Upload History Button */}
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => {
                          setHistorialDialogOpen(true);
                          loadHistorial();
                        }}
                        data-testid="historial-btn"
                      >
                        <History className="w-4 h-4 sm:mr-2" />
                        <span className="hidden sm:inline">Historial</span>
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Ver historial de cargas de archivos</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>

                {/* Upload History Dialog */}
                <Dialog open={historialDialogOpen} onOpenChange={setHistorialDialogOpen}>
                  <DialogContent className="sm:max-w-5xl max-h-[80vh]">
                    <DialogHeader>
                      <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>
                        📜 Historial de Cargas
                      </DialogTitle>
                    </DialogHeader>
                    <div className="py-4">
                      {loadingHistorial ? (
                        <div className="flex justify-center py-8">
                          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
                        </div>
                      ) : historialCargas.length === 0 ? (
                        <div className="text-center py-8 text-muted-foreground">
                          <History className="w-12 h-12 mx-auto mb-4 opacity-50" />
                          <p>No hay cargas registradas</p>
                        </div>
                      ) : (
                        <div className="overflow-x-auto max-h-96">
                          <table className="w-full text-sm">
                            <thead className="bg-slate-50 sticky top-0">
                              <tr>
                                <th className="px-3 py-2 text-left">Fecha</th>
                                <th className="px-3 py-2 text-left">Archivo</th>
                                <th className="px-3 py-2 text-left">Usuario</th>
                                <th className="px-3 py-2 text-center">Registros (DB/Excel)</th>
                                <th className="px-3 py-2 text-right">Ingresos</th>
                                <th className="px-3 py-2 text-right">Egresos</th>
                                <th className="px-3 py-2 text-center">Estado</th>
                                <th className="px-3 py-2 text-center">Acciones</th>
                              </tr>
                            </thead>
                            <tbody>
                              {historialCargas.map((carga) => (
                                <tr key={carga.id} className="border-t hover:bg-slate-50">
                                  <td className="px-3 py-2">
                                    <div className="flex items-center gap-1">
                                      <Calendar className="w-3 h-3 text-muted-foreground" />
                                      {(() => {
                                        const d = new Date(carga.fecha_carga);
                                        return `${String(d.getDate()).padStart(2,'0')}/${String(d.getMonth()+1).padStart(2,'0')}/${d.getFullYear()} ${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`;
                                      })()}
                                    </div>
                                  </td>
                                  <td className="px-3 py-2 max-w-xs truncate" title={carga.filename}>
                                    <FileSpreadsheet className="w-4 h-4 inline mr-1 text-green-600" />
                                    {carga.filename}
                                  </td>
                                  <td className="px-3 py-2">{carga.usuario_nombre || carga.usuario_email}</td>
                                  <td className="px-3 py-2 text-center">
                                    <div className="flex flex-col items-center">
                                      <span className="font-medium">
                                        {carga.db_registros || carga.registros_cargados || 0}
                                        <span className="text-muted-foreground text-xs"> / {carga.excel_registros || carga.registros_totales || 0}</span>
                                      </span>
                                      {carga.registros_rechazados > 0 && (
                                        <span className="text-orange-500 text-xs">{carga.registros_rechazados} duplicados</span>
                                      )}
                                    </div>
                                  </td>
                                  <td className="px-3 py-2 text-right">
                                    <div className="flex flex-col items-end">
                                      <span className="font-mono text-green-600">{formatCurrency(carga.db_ingresos || carga.total_ingresos || 0)}</span>
                                      {carga.excel_ingresos && (
                                        <span className="text-xs text-muted-foreground">Excel: {formatCurrency(carga.excel_ingresos)}</span>
                                      )}
                                    </div>
                                  </td>
                                  <td className="px-3 py-2 text-right">
                                    <div className="flex flex-col items-end">
                                      <span className="font-mono text-red-600">{formatCurrency(carga.db_egresos || carga.total_egresos || 0)}</span>
                                      {carga.excel_egresos && (
                                        <span className="text-xs text-muted-foreground">Excel: {formatCurrency(carga.excel_egresos)}</span>
                                      )}
                                    </div>
                                  </td>
                                  <td className="px-3 py-2 text-center">
                                    {carga.verificacion?.integridad_ok ? (
                                      <Badge variant="success" className="gap-1">
                                        <CheckCircle className="w-3 h-3" />
                                        Verificado
                                      </Badge>
                                    ) : carga.resultado === 'exitoso' ? (
                                      <Badge variant="success">✓ OK</Badge>
                                    ) : carga.resultado === 'error_integridad' ? (
                                      <Badge variant="destructive" className="gap-1">
                                        <AlertCircle className="w-3 h-3" />
                                        Error
                                      </Badge>
                                    ) : (
                                      <Badge variant="secondary">Sin nuevos</Badge>
                                    )}
                                  </td>
                                  <td className="px-3 py-2 text-center">
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="h-8 w-8 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                                      onClick={() => handleDeleteCarga(carga)}
                                      disabled={deletingCarga === carga.id}
                                      title="Eliminar carga y sus movimientos"
                                    >
                                      {deletingCarga === carga.id ? (
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                      ) : (
                                        <Trash2 className="w-4 h-4" />
                                      )}
                                    </Button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                    <DialogFooter className="gap-2">
                      <Button variant="outline" onClick={() => setHistorialDialogOpen(false)}>
                        Cerrar
                      </Button>
                      <Button 
                        variant="destructive" 
                        size="sm"
                        onClick={() => setDeleteAllDialogOpen(true)}
                        className="gap-2"
                      >
                        <Trash2 className="w-4 h-4" />
                        Eliminar Todo
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>

                {/* Delete ALL Movements Confirmation Dialog */}
                <Dialog open={deleteAllDialogOpen} onOpenChange={(open) => {
                  setDeleteAllDialogOpen(open);
                  if (!open) setDeleteAllConfirmText('');
                }}>
                  <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                      <DialogTitle className="flex items-center gap-2 text-red-600">
                        <AlertCircle className="w-6 h-6" />
                        ⚠️ ELIMINAR TODOS LOS MOVIMIENTOS
                      </DialogTitle>
                    </DialogHeader>
                    <div className="py-4 space-y-4">
                      <div className="bg-red-100 border-2 border-red-400 rounded-lg p-4">
                        <p className="font-bold text-red-800 text-center mb-2">
                          ¡ATENCIÓN! ACCIÓN IRREVERSIBLE
                        </p>
                        <p className="text-sm text-red-700 text-center">
                          Esto eliminará <strong>TODOS</strong> los movimientos financieros y el historial de cargas de tu empresa.
                        </p>
                      </div>
                      
                      <div className="bg-slate-100 rounded-lg p-4 text-center">
                        <p className="text-lg font-bold">{totalCount.toLocaleString()}</p>
                        <p className="text-sm text-muted-foreground">movimientos serán eliminados</p>
                        <div className="flex justify-center gap-6 mt-2 text-sm">
                          <span className="text-green-600">Ingresos: {formatCurrency(globalTotals.ingresos)}</span>
                          <span className="text-red-600">Egresos: {formatCurrency(globalTotals.egresos)}</span>
                        </div>
                      </div>
                      
                      <div>
                        <p className="text-sm text-muted-foreground mb-2">
                          Para confirmar, escribe <strong className="text-red-600">ELIMINAR TODO</strong> en el campo:
                        </p>
                        <Input
                          placeholder="Escribe ELIMINAR TODO"
                          value={deleteAllConfirmText}
                          onChange={(e) => setDeleteAllConfirmText(e.target.value)}
                          className="text-center font-mono"
                          data-testid="delete-all-confirm-input"
                        />
                      </div>
                    </div>
                    <DialogFooter className="gap-2">
                      <Button 
                        variant="outline" 
                        onClick={() => {
                          setDeleteAllDialogOpen(false);
                          setDeleteAllConfirmText('');
                        }}
                      >
                        Cancelar
                      </Button>
                      <Button 
                        variant="destructive" 
                        onClick={handleDeleteAll}
                        disabled={deletingAll || deleteAllConfirmText !== 'ELIMINAR TODO'}
                      >
                        {deletingAll ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <Trash2 className="w-4 h-4 mr-2" />
                        )}
                        Eliminar Todo Permanentemente
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>

                {/* Delete Confirmation Dialog for Historial */}
                <Dialog open={deleteHistorialDialogOpen} onOpenChange={setDeleteHistorialDialogOpen}>
                  <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                      <DialogTitle className="flex items-center gap-2 text-red-600">
                        <AlertCircle className="w-5 h-5" />
                        Confirmar Eliminación
                      </DialogTitle>
                    </DialogHeader>
                    <div className="py-4">
                      <p className="text-sm text-muted-foreground mb-4">
                        ¿Estás seguro de que deseas eliminar esta carga? Esta acción:
                      </p>
                      <div className="bg-red-50 border border-red-200 rounded-lg p-4 space-y-2">
                        <p className="font-medium text-red-800">
                          📄 {cargaToDelete?.filename}
                        </p>
                        <p className="text-sm text-red-700">
                          • Eliminará <strong>{cargaToDelete?.db_registros || cargaToDelete?.registros_cargados || 0}</strong> movimientos de la base de datos
                        </p>
                        <p className="text-sm text-red-700">
                          • Ingresos: {formatCurrency(cargaToDelete?.db_ingresos || cargaToDelete?.total_ingresos || 0)}
                        </p>
                        <p className="text-sm text-red-700">
                          • Egresos: {formatCurrency(cargaToDelete?.db_egresos || cargaToDelete?.total_egresos || 0)}
                        </p>
                      </div>
                      <p className="text-sm text-red-600 font-medium mt-4">
                        ⚠️ Esta acción no se puede deshacer
                      </p>
                    </div>
                    <DialogFooter className="gap-2">
                      <Button 
                        variant="outline" 
                        onClick={() => {
                          setDeleteHistorialDialogOpen(false);
                          setCargaToDelete(null);
                        }}
                      >
                        Cancelar
                      </Button>
                      <Button 
                        variant="destructive" 
                        onClick={confirmDeleteCarga}
                        disabled={deletingCarga}
                      >
                        {deletingCarga ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <Trash2 className="w-4 h-4 mr-2" />
                        )}
                        Eliminar Carga
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>

            {/* Create Movement Dialog */}
            <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <DialogTrigger asChild>
                      <Button size="sm" onClick={resetNewMov} data-testid="create-mov-btn">
                        <Plus className="w-4 h-4 sm:mr-2" />
                        <span className="hidden sm:inline">Nuevo</span>
                      </Button>
                    </DialogTrigger>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Registrar un nuevo movimiento financiero manualmente</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
              <DialogContent className="sm:max-w-md">
                <DialogHeader>
                  <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>
                    Nuevo Movimiento
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Fecha *</Label>
                      <Input 
                        type="date" 
                        value={newMov.fecha} 
                        onChange={(e) => setNewMov({...newMov, fecha: e.target.value})}
                        data-testid="new-mov-fecha"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Tipo *</Label>
                      <Select 
                        value={newMov.tipo} 
                        onValueChange={(v) => setNewMov({...newMov, tipo: v})}
                      >
                        <SelectTrigger data-testid="new-mov-tipo">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="ingreso">Ingreso</SelectItem>
                          <SelectItem value="egreso">Egreso</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Concepto *</Label>
                    <Input 
                      value={newMov.concepto} 
                      onChange={(e) => setNewMov({...newMov, concepto: e.target.value})}
                      placeholder="Descripción del movimiento"
                      data-testid="new-mov-concepto"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Cliente/Proveedor</Label>
                    <Input 
                      value={newMov.cliente_proveedor} 
                      onChange={(e) => setNewMov({...newMov, cliente_proveedor: e.target.value})}
                      placeholder="Nombre del cliente o proveedor"
                      data-testid="new-mov-cliente"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Cuenta *</Label>
                      <Select 
                        value={newMov.cuenta_id} 
                        onValueChange={(v) => setNewMov({...newMov, cuenta_id: v})}
                      >
                        <SelectTrigger data-testid="new-mov-cuenta">
                          <SelectValue placeholder="Seleccionar" />
                        </SelectTrigger>
                        <SelectContent>
                          {cuentasList.map((cuenta) => (
                            <SelectItem key={cuenta.id} value={cuenta.id}>
                              {cuenta.nombre}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Monto *</Label>
                      <Input 
                        type="number" 
                        value={newMov.monto} 
                        onChange={(e) => setNewMov({...newMov, monto: e.target.value})}
                        placeholder="$0.00"
                        data-testid="new-mov-monto"
                      />
                    </div>
                  </div>
                  {newMov.tipo === 'egreso' && (
                    <div className="space-y-2">
                      <Label>Categoría</Label>
                      <Select 
                        value={newMov.categoria_id} 
                        onValueChange={(v) => setNewMov({...newMov, categoria_id: v})}
                      >
                        <SelectTrigger data-testid="new-mov-categoria">
                          <SelectValue placeholder="Sin categoría" />
                        </SelectTrigger>
                        <SelectContent>
                          {categoriasList.map((cat) => (
                            <SelectItem key={cat.id} value={cat.id}>
                              <div className="flex items-center gap-2">
                                <div className="w-2 h-2 rounded-full" style={{ backgroundColor: cat.color }} />
                                {cat.nombre}
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>Cancelar</Button>
                  <Button onClick={handleCreateMov} disabled={saving}>
                    {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                    <Save className="w-4 h-4 mr-2" />
                    Guardar
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="page-content">
        {/* Mobile Filters Button */}
        <div className="md:hidden mb-4">
          <Sheet open={mobileFiltersOpen} onOpenChange={setMobileFiltersOpen}>
            <SheetTrigger asChild>
              <Button variant="outline" className="w-full gap-2" data-testid="mobile-filters-btn">
                <SlidersHorizontal className="w-4 h-4" />
                Filtros
                {(filters.cuenta_id || filters.tipo || filters.categoria_id || filters.cliente_proveedor || 
                  filters.concepto || filters.fecha_inicio || filters.fecha_fin || 
                  excludedCategorias.length > 0 || excludedClientes.length > 0) && (
                  <Badge variant="secondary" className="ml-1">Activos</Badge>
                )}
              </Button>
            </SheetTrigger>
            <SheetContent side="bottom" className="h-[85vh] overflow-y-auto">
              <SheetHeader className="mb-4">
                <SheetTitle className="flex items-center gap-2">
                  <Filter className="w-5 h-5" />
                  Filtros de búsqueda
                </SheetTitle>
              </SheetHeader>
              
              <div className="space-y-4 pb-20">
                {/* Basic Filters */}
                <div className="space-y-3">
                  <div className="space-y-2">
                    <Label>Cuenta</Label>
                    <Select 
                      value={filters.cuenta_id} 
                      onValueChange={(v) => setFilters({ ...filters, cuenta_id: v })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Todas" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Consolidado</SelectItem>
                        {cuentasList.map((cuenta) => (
                          <SelectItem key={cuenta.id} value={cuenta.id}>
                            {cuenta.nombre}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Tipo</Label>
                    <Select 
                      value={filters.tipo} 
                      onValueChange={(v) => setFilters({ ...filters, tipo: v })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Todos" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todos</SelectItem>
                        <SelectItem value="ingreso">Ingresos</SelectItem>
                        <SelectItem value="egreso">Egresos</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Categoría</Label>
                    <Select 
                      value={filters.categoria_id} 
                      onValueChange={(v) => setFilters({ ...filters, categoria_id: v })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Todas" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todas</SelectItem>
                        {categoriasList.map((cat) => (
                          <SelectItem key={cat.id} value={cat.id}>
                            <div className="flex items-center gap-2">
                              <div 
                                className="w-3 h-3 rounded-full" 
                                style={{ backgroundColor: cat.color }}
                              />
                              {cat.nombre}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Cliente/Proveedor</Label>
                    <Select 
                      value={filters.cliente_proveedor} 
                      onValueChange={(v) => setFilters({ ...filters, cliente_proveedor: v })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Todos" />
                      </SelectTrigger>
                      <SelectContent className="max-h-64">
                        <SelectItem value="all">Todos</SelectItem>
                        {uniqueClientes.map((cliente) => (
                          <SelectItem key={cliente} value={cliente}>
                            <span className="truncate max-w-[200px]" title={cliente}>
                              {cliente}
                            </span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Buscar concepto</Label>
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        placeholder="Buscar..."
                        className="pl-10"
                        value={filters.concepto}
                        onChange={(e) => setFilters({ ...filters, concepto: e.target.value })}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label>Desde</Label>
                      <Input
                        type="date"
                        value={filters.fecha_inicio}
                        onChange={(e) => setFilters({ ...filters, fecha_inicio: e.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Hasta</Label>
                      <Input
                        type="date"
                        value={filters.fecha_fin}
                        onChange={(e) => setFilters({ ...filters, fecha_fin: e.target.value })}
                      />
                    </div>
                  </div>
                </div>

                {/* Exclusion Filters Section */}
                <div className="pt-4 border-t">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowExclusionFilters(!showExclusionFilters)}
                    className="w-full gap-2 mb-4"
                  >
                    <FilterX className="w-4 h-4" />
                    {showExclusionFilters ? 'Ocultar' : 'Mostrar'} filtros de exclusión
                    {(excludedCategorias.length > 0 || excludedClientes.length > 0) && (
                      <Badge variant="secondary" className="ml-1">
                        {excludedCategorias.length + excludedClientes.length}
                      </Badge>
                    )}
                  </Button>
                  
                  {showExclusionFilters && (
                    <div className="space-y-4">
                      {/* Exclude Categories (Egresos) */}
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <Label className="flex items-center gap-2 text-red-700 text-sm">
                            <Tags className="w-4 h-4" />
                            Omitir Categorías (Egresos)
                          </Label>
                          {excludedCategorias.length > 0 && (
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              className="h-6 text-xs"
                              onClick={() => setExcludedCategorias([])}
                            >
                              Limpiar ({excludedCategorias.length})
                            </Button>
                          )}
                        </div>
                        <div className="max-h-36 overflow-y-auto border rounded-lg p-2 bg-slate-50">
                          {categoriasList.length === 0 ? (
                            <p className="text-sm text-muted-foreground text-center py-2">No hay categorías</p>
                          ) : (
                            <div className="space-y-1">
                              {categoriasList.map((cat) => (
                                <label 
                                  key={cat.id} 
                                  className="flex items-center gap-2 p-2 hover:bg-white rounded cursor-pointer"
                                >
                                  <Checkbox
                                    checked={excludedCategorias.includes(cat.id)}
                                    onCheckedChange={(checked) => {
                                      if (checked) {
                                        setExcludedCategorias(prev => [...prev, cat.id]);
                                      } else {
                                        setExcludedCategorias(prev => prev.filter(id => id !== cat.id));
                                      }
                                    }}
                                    className="border-red-300 data-[state=checked]:bg-red-600"
                                  />
                                  <div 
                                    className="w-2 h-2 rounded-full flex-shrink-0" 
                                    style={{ backgroundColor: cat.color }}
                                  />
                                  <span className="text-sm truncate">{cat.nombre}</span>
                                </label>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                      
                      {/* Exclude Clientes (Ingresos) */}
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <Label className="flex items-center gap-2 text-emerald-700 text-sm">
                            <TrendingUp className="w-4 h-4" />
                            Omitir Clientes (Ingresos)
                          </Label>
                          {excludedClientes.filter(c => uniqueClientesIngresos.includes(c)).length > 0 && (
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              className="h-6 text-xs"
                              onClick={() => setExcludedClientes(prev => prev.filter(c => !uniqueClientesIngresos.includes(c)))}
                            >
                              Limpiar
                            </Button>
                          )}
                        </div>
                        <div className="max-h-32 overflow-y-auto border rounded-lg p-2 bg-slate-50">
                          {uniqueClientesIngresos.length === 0 ? (
                            <p className="text-sm text-muted-foreground text-center py-2">
                              Sin clientes
                            </p>
                          ) : (
                            <div className="space-y-1">
                              {uniqueClientesIngresos.map((cliente) => (
                                <label 
                                  key={cliente} 
                                  className="flex items-center gap-2 p-2 hover:bg-white rounded cursor-pointer"
                                >
                                  <Checkbox
                                    checked={excludedClientes.includes(cliente)}
                                    onCheckedChange={(checked) => {
                                      if (checked) {
                                        setExcludedClientes(prev => [...prev, cliente]);
                                      } else {
                                        setExcludedClientes(prev => prev.filter(c => c !== cliente));
                                      }
                                    }}
                                    className="border-emerald-300 data-[state=checked]:bg-emerald-600"
                                  />
                                  <span className="text-sm truncate" title={cliente}>
                                    {cliente}
                                  </span>
                                </label>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                      
                      {/* Exclude Proveedores (Egresos) */}
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <Label className="flex items-center gap-2 text-orange-700 text-sm">
                            <TrendingDown className="w-4 h-4" />
                            Omitir Proveedores (Egresos)
                          </Label>
                          {excludedClientes.filter(c => uniqueClientesEgresos.includes(c)).length > 0 && (
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              className="h-6 text-xs"
                              onClick={() => setExcludedClientes(prev => prev.filter(c => !uniqueClientesEgresos.includes(c)))}
                            >
                              Limpiar
                            </Button>
                          )}
                        </div>
                        <div className="max-h-32 overflow-y-auto border rounded-lg p-2 bg-slate-50">
                          {uniqueClientesEgresos.length === 0 ? (
                            <p className="text-sm text-muted-foreground text-center py-2">
                              Sin proveedores
                            </p>
                          ) : (
                            <div className="space-y-1">
                              {uniqueClientesEgresos.map((cliente) => (
                                <label 
                                  key={cliente} 
                                  className="flex items-center gap-2 p-2 hover:bg-white rounded cursor-pointer"
                                >
                                  <Checkbox
                                    checked={excludedClientes.includes(cliente)}
                                    onCheckedChange={(checked) => {
                                      if (checked) {
                                        setExcludedClientes(prev => [...prev, cliente]);
                                      } else {
                                        setExcludedClientes(prev => prev.filter(c => c !== cliente));
                                      }
                                    }}
                                    className="border-orange-300 data-[state=checked]:bg-orange-600"
                                  />
                                  <span className="text-sm truncate" title={cliente}>
                                    {cliente}
                                  </span>
                                </label>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Apply Button */}
                <div className="pt-4">
                  <Button 
                    className="w-full" 
                    onClick={() => setMobileFiltersOpen(false)}
                  >
                    Aplicar Filtros
                  </Button>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>

        {/* Desktop Filters */}
        <Card className="mb-6 hidden md:block">
          <CardContent className="pt-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-7 gap-4">
              <div className="space-y-2">
                <Label>Cuenta</Label>
                <Select 
                  value={filters.cuenta_id} 
                  onValueChange={(v) => setFilters({ ...filters, cuenta_id: v })}
                >
                  <SelectTrigger data-testid="filter-cuenta">
                    <SelectValue placeholder="Todas" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Consolidado</SelectItem>
                    {cuentasList.map((cuenta) => (
                      <SelectItem key={cuenta.id} value={cuenta.id}>
                        {cuenta.nombre}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Tipo</Label>
                <Select 
                  value={filters.tipo} 
                  onValueChange={(v) => setFilters({ ...filters, tipo: v })}
                >
                  <SelectTrigger data-testid="filter-tipo">
                    <SelectValue placeholder="Todos" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    <SelectItem value="ingreso">Ingresos</SelectItem>
                    <SelectItem value="egreso">Egresos</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Categoría</Label>
                <Select 
                  value={filters.categoria_id} 
                  onValueChange={(v) => setFilters({ ...filters, categoria_id: v })}
                >
                  <SelectTrigger data-testid="filter-categoria">
                    <SelectValue placeholder="Todas" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todas</SelectItem>
                    {categoriasList.map((cat) => (
                      <SelectItem key={cat.id} value={cat.id}>
                        <div className="flex items-center gap-2">
                          <div 
                            className="w-3 h-3 rounded-full" 
                            style={{ backgroundColor: cat.color }}
                          />
                          {cat.nombre}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Cliente/Proveedor</Label>
                <Select 
                  value={filters.cliente_proveedor} 
                  onValueChange={(v) => setFilters({ ...filters, cliente_proveedor: v })}
                >
                  <SelectTrigger data-testid="filter-cliente-proveedor">
                    <Users className="w-4 h-4 mr-2 text-muted-foreground" />
                    <SelectValue placeholder="Todos" />
                  </SelectTrigger>
                  <SelectContent className="max-h-64">
                    <SelectItem value="all">Todos</SelectItem>
                    {uniqueClientes.map((cliente) => (
                      <SelectItem key={cliente} value={cliente}>
                        <span className="truncate max-w-[200px]" title={cliente}>
                          {cliente}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Buscar concepto</Label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Buscar..."
                    className="pl-10"
                    value={filters.concepto}
                    onChange={(e) => setFilters({ ...filters, concepto: e.target.value })}
                    data-testid="filter-concepto"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Desde</Label>
                <Input
                  type="date"
                  value={filters.fecha_inicio}
                  onChange={(e) => setFilters({ ...filters, fecha_inicio: e.target.value })}
                  data-testid="filter-fecha-inicio"
                />
              </div>

              <div className="space-y-2">
                <Label>Hasta</Label>
                <Input
                  type="date"
                  value={filters.fecha_fin}
                  onChange={(e) => setFilters({ ...filters, fecha_fin: e.target.value })}
                  data-testid="filter-fecha-fin"
                />
              </div>
            </div>
            
            {/* Exclusion Filters Toggle */}
            <div className="mt-4 pt-4 border-t">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowExclusionFilters(!showExclusionFilters)}
                className="gap-2"
              >
                <FilterX className="w-4 h-4" />
                {showExclusionFilters ? 'Ocultar' : 'Mostrar'} filtros de exclusión
                {(excludedCategorias.length > 0 || excludedClientes.length > 0) && (
                  <Badge variant="secondary" className="ml-1">
                    {excludedCategorias.length + excludedClientes.length}
                  </Badge>
                )}
              </Button>
              
              {/* Active Exclusions Summary */}
              {(excludedCategorias.length > 0 || excludedClientes.length > 0) && !showExclusionFilters && (
                <div className="flex flex-wrap gap-2 mt-3">
                  {excludedCategorias.map(catId => {
                    const cat = categoriasList.find(c => c.id === catId);
                    return cat ? (
                      <Badge key={catId} variant="outline" className="gap-1 bg-red-50 border-red-200 text-red-700">
                        <Tags className="w-3 h-3" />
                        {cat.nombre}
                        <button 
                          onClick={() => setExcludedCategorias(prev => prev.filter(id => id !== catId))}
                          className="ml-1 hover:bg-red-200 rounded-full p-0.5"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </Badge>
                    ) : null;
                  })}
                  {excludedClientes.map(cliente => (
                    <Badge key={cliente} variant="outline" className="gap-1 bg-orange-50 border-orange-200 text-orange-700">
                      <Users className="w-3 h-3" />
                      {cliente.length > 20 ? cliente.substring(0, 20) + '...' : cliente}
                      <button 
                        onClick={() => setExcludedClientes(prev => prev.filter(c => c !== cliente))}
                        className="ml-1 hover:bg-orange-200 rounded-full p-0.5"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </Badge>
                  ))}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setExcludedCategorias([]);
                      setExcludedClientes([]);
                    }}
                    className="text-xs text-muted-foreground h-6"
                  >
                    Limpiar todo
                  </Button>
                </div>
              )}
            </div>
            
            {/* Exclusion Filters Panel */}
            {showExclusionFilters && (
              <div className="mt-4 pt-4 border-t space-y-4">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  {/* Exclude Categories (Egresos) */}
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <Label className="flex items-center gap-2 text-red-700">
                        <Tags className="w-4 h-4" />
                        Omitir Categorías (Egresos)
                      </Label>
                      {excludedCategorias.length > 0 && (
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="h-6 text-xs"
                          onClick={() => setExcludedCategorias([])}
                        >
                          Limpiar ({excludedCategorias.length})
                        </Button>
                      )}
                    </div>
                    <div className="max-h-48 overflow-y-auto border rounded-lg p-2 bg-slate-50">
                      {categoriasList.length === 0 ? (
                        <p className="text-sm text-muted-foreground text-center py-4">No hay categorías</p>
                      ) : (
                        <div className="space-y-2">
                          {categoriasList.map((cat) => (
                            <label 
                              key={cat.id} 
                              className="flex items-center gap-3 p-2 hover:bg-white rounded cursor-pointer transition-colors"
                            >
                              <Checkbox
                                checked={excludedCategorias.includes(cat.id)}
                                onCheckedChange={(checked) => {
                                  if (checked) {
                                    setExcludedCategorias(prev => [...prev, cat.id]);
                                  } else {
                                    setExcludedCategorias(prev => prev.filter(id => id !== cat.id));
                                  }
                                }}
                                className="border-red-300 data-[state=checked]:bg-red-600 data-[state=checked]:border-red-600"
                              />
                              <div 
                                className="w-3 h-3 rounded-full flex-shrink-0" 
                                style={{ backgroundColor: cat.color }}
                              />
                              <span className="text-sm flex-1 truncate">{cat.nombre}</span>
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Exclude Clientes (Ingresos) */}
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <Label className="flex items-center gap-2 text-emerald-700">
                        <TrendingUp className="w-4 h-4" />
                        Omitir Clientes (Ingresos)
                      </Label>
                      {excludedClientes.filter(c => uniqueClientesIngresos.includes(c)).length > 0 && (
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="h-6 text-xs"
                          onClick={() => setExcludedClientes(prev => prev.filter(c => !uniqueClientesIngresos.includes(c)))}
                        >
                          Limpiar
                        </Button>
                      )}
                    </div>
                    <div className="max-h-48 overflow-y-auto border rounded-lg p-2 bg-emerald-50">
                      {uniqueClientesIngresos.length === 0 ? (
                        <p className="text-sm text-muted-foreground text-center py-4">
                          Sin clientes de ingresos
                        </p>
                      ) : (
                        <div className="space-y-2">
                          {uniqueClientesIngresos.map((cliente) => (
                            <label 
                              key={cliente} 
                              className="flex items-center gap-3 p-2 hover:bg-white rounded cursor-pointer transition-colors"
                            >
                              <Checkbox
                                checked={excludedClientes.includes(cliente)}
                                onCheckedChange={(checked) => {
                                  if (checked) {
                                    setExcludedClientes(prev => [...prev, cliente]);
                                  } else {
                                    setExcludedClientes(prev => prev.filter(c => c !== cliente));
                                  }
                                }}
                                className="border-emerald-300 data-[state=checked]:bg-emerald-600 data-[state=checked]:border-emerald-600"
                              />
                              <span className="text-sm flex-1 truncate" title={cliente}>
                                {cliente}
                              </span>
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Exclude Proveedores (Egresos) */}
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <Label className="flex items-center gap-2 text-orange-700">
                        <TrendingDown className="w-4 h-4" />
                        Omitir Proveedores (Egresos)
                      </Label>
                      {excludedClientes.filter(c => uniqueClientesEgresos.includes(c)).length > 0 && (
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="h-6 text-xs"
                          onClick={() => setExcludedClientes(prev => prev.filter(c => !uniqueClientesEgresos.includes(c)))}
                        >
                          Limpiar
                        </Button>
                      )}
                    </div>
                    <div className="max-h-48 overflow-y-auto border rounded-lg p-2 bg-orange-50">
                      {uniqueClientesEgresos.length === 0 ? (
                        <p className="text-sm text-muted-foreground text-center py-4">
                          Sin proveedores de egresos
                        </p>
                      ) : (
                        <div className="space-y-2">
                          {uniqueClientesEgresos.map((cliente) => (
                            <label 
                              key={cliente} 
                              className="flex items-center gap-3 p-2 hover:bg-white rounded cursor-pointer transition-colors"
                            >
                              <Checkbox
                                checked={excludedClientes.includes(cliente)}
                                onCheckedChange={(checked) => {
                                  if (checked) {
                                    setExcludedClientes(prev => [...prev, cliente]);
                                  } else {
                                    setExcludedClientes(prev => prev.filter(c => c !== cliente));
                                  }
                                }}
                                className="border-orange-300 data-[state=checked]:bg-orange-600 data-[state=checked]:border-orange-600"
                              />
                              <span className="text-sm flex-1 truncate" title={cliente}>
                                {cliente}
                              </span>
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Summary Cards */}
        <div className="summary-cards-grid mb-6">
          <Card className="bg-emerald-50 border-emerald-200 summary-card">
            <CardContent className="p-0">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs sm:text-sm text-emerald-600">Ingresos</p>
                  <p className="text-lg sm:text-2xl font-bold text-emerald-700 tabular-nums" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    {formatCurrency(totalIngresos)}
                  </p>
                </div>
                <ArrowUpRight className="w-6 h-6 sm:w-8 sm:h-8 text-emerald-500" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-red-50 border-red-200 summary-card">
            <CardContent className="p-0">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs sm:text-sm text-red-600">Egresos</p>
                  <p className="text-lg sm:text-2xl font-bold text-red-700 tabular-nums" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    {formatCurrency(totalEgresos)}
                  </p>
                </div>
                <ArrowDownRight className="w-6 h-6 sm:w-8 sm:h-8 text-red-500" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-primary/5 border-primary/20 summary-card col-span-2 sm:col-span-1">
            <CardContent className="p-0">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs sm:text-sm text-primary">Movimientos</p>
                  <p className="text-lg sm:text-2xl font-bold text-primary" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    {totalCount > 0 ? totalCount.toLocaleString() : movimientosList.length}
                  </p>
                </div>
                <Receipt className="w-6 h-6 sm:w-8 sm:h-8 text-primary/50" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Mobile Card View */}
        <div className="data-table-mobile">
          {loading ? (
            Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="skeleton h-32 rounded-xl"></div>
            ))
          ) : sortedMovimientos.length === 0 ? (
            <div className="empty-state">
              <Receipt className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">No hay movimientos</p>
              <p className="text-sm text-muted-foreground">Carga un archivo Excel para comenzar</p>
            </div>
          ) : (
            <>
              {sortedMovimientos.map((mov) => (
                <div key={mov.id} className="table-mobile-card" data-testid={`mov-card-${mov.id}`}>
                  <div className="table-mobile-card-header">
                    <div className="flex-1 min-w-0">
                      <div className="table-mobile-card-title truncate">
                        {mov.concepto || <span className="text-muted-foreground italic">(Sin concepto)</span>}
                      </div>
                      <p className="text-xs text-slate-500 mt-1">{mov.cliente_proveedor || 'Sin cliente'}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge className={mov.tipo === 'ingreso' ? 'badge-ingreso' : mov.tipo === 'neutro' ? 'bg-gray-200 text-gray-600' : 'badge-egreso'}>
                        {mov.tipo === 'ingreso' ? 'Ingreso' : mov.tipo === 'neutro' ? '$0' : 'Egreso'}
                      </Badge>
                      {isAdmin && (
                        <div className="flex gap-1">
                          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEditDialog(mov)}>
                            <Edit2 className="w-3 h-3" />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => handleDelete(mov.id)}>
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="table-mobile-card-row">
                    <label>Monto</label>
                    <span className={`font-bold ${mov.tipo === 'ingreso' ? 'text-emerald-600' : mov.tipo === 'neutro' ? 'text-gray-500' : 'text-red-600'}`}>
                      {mov.tipo === 'ingreso' ? '+' : mov.tipo === 'neutro' ? '' : '-'}{formatCurrency(mov.monto)}
                    </span>
                  </div>
                  
                  <div className="table-mobile-card-row">
                    <label>Fecha</label>
                    <span>{formatDate(mov.fecha)}</span>
                  </div>
                  
                  <div className="table-mobile-card-row">
                    <label>Cuenta</label>
                    <Badge variant="outline" className="text-xs">{mov.cuenta_nombre}</Badge>
                  </div>
                  
                  {mov.tipo === 'egreso' && (
                    <div className="mt-2 pt-2 border-t border-slate-100">
                      <label className="text-xs text-slate-500 mb-1 block">Categoría:</label>
                      <Select
                        value={mov.categoria_id || ''}
                        onValueChange={(v) => handleCategoryChange(mov.id, v)}
                      >
                        <SelectTrigger className="w-full h-9">
                          <SelectValue>
                            {mov.categoria_nombre ? (
                              <div className="flex items-center gap-2">
                                <div 
                                  className="w-2 h-2 rounded-full" 
                                  style={{ backgroundColor: categoriasList.find(c => c.id === mov.categoria_id)?.color }}
                                />
                                <span className="truncate">{mov.categoria_nombre}</span>
                              </div>
                            ) : (
                              <span className="text-muted-foreground">Sin categoría</span>
                            )}
                          </SelectValue>
                        </SelectTrigger>
                        <SelectContent position="popper" sideOffset={4}>
                          {categoriasList.map((cat) => (
                            <SelectItem key={cat.id} value={cat.id}>
                              <div className="flex items-center gap-2">
                                <div className="w-2 h-2 rounded-full" style={{ backgroundColor: cat.color }} />
                                {cat.nombre}
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>
              ))}
              
              {/* Mobile Pagination */}
              {totalCount > pageSize && (
                <div className="flex flex-col items-center gap-3 py-4 mt-2 border-t bg-white rounded-lg">
                  <span className="text-xs text-muted-foreground">
                    {((currentPage - 1) * pageSize) + 1}-{Math.min(currentPage * pageSize, totalCount)} de {totalCount.toLocaleString()}
                  </span>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                      className="gap-1"
                    >
                      <ChevronLeft className="w-4 h-4" />
                      Anterior
                    </Button>
                    <span className="text-sm px-3 font-medium">
                      {currentPage} / {Math.ceil(totalCount / pageSize)}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(p => Math.min(Math.ceil(totalCount / pageSize), p + 1))}
                      disabled={currentPage >= Math.ceil(totalCount / pageSize)}
                      className="gap-1"
                    >
                      Siguiente
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Desktop Table */}
        <Card className="table-container data-table-desktop">
          <div className="overflow-x-auto">
            <table className="data-table text-xs" data-testid="movimientos-table">
              <thead>
                <tr>
                  {isAdmin && (
                    <th className="px-2 py-2 w-10">
                      <input
                        type="checkbox"
                        className="rounded border-gray-300 cursor-pointer"
                        checked={selectedIds.size === sortedMovimientos.length && sortedMovimientos.length > 0}
                        onChange={(e) => handleSelectAll(e.target.checked)}
                        data-testid="select-all-checkbox"
                      />
                    </th>
                  )}
                  <th className="cursor-pointer select-none px-2 py-2" onClick={() => handleSort('fecha')}>
                    <div className="flex items-center">Fecha {getSortIcon('fecha')}</div>
                  </th>
                  <th className="cursor-pointer select-none px-2 py-2" onClick={() => handleSort('concepto')}>
                    <div className="flex items-center">Concepto {getSortIcon('concepto')}</div>
                  </th>
                  <th className="cursor-pointer select-none px-2 py-2" onClick={() => handleSort('cliente_proveedor')}>
                    <div className="flex items-center">Cliente/Prov. {getSortIcon('cliente_proveedor')}</div>
                  </th>
                  <th className="cursor-pointer select-none px-2 py-2" onClick={() => handleSort('tipo')}>
                    <div className="flex items-center">Tipo {getSortIcon('tipo')}</div>
                  </th>
                  <th className="cursor-pointer select-none px-2 py-2" onClick={() => handleSort('categoria_nombre')}>
                    <div className="flex items-center">Categoría {getSortIcon('categoria_nombre')}</div>
                  </th>
                  <th className="cursor-pointer select-none px-2 py-2" onClick={() => handleSort('cuenta_nombre')}>
                    <div className="flex items-center">Cuenta {getSortIcon('cuenta_nombre')}</div>
                  </th>
                  <th className="cursor-pointer select-none text-right px-2 py-2" onClick={() => handleSort('monto')}>
                    <div className="flex items-center justify-end">Monto {getSortIcon('monto')}</div>
                  </th>
                  {isAdmin && <th className="px-2 py-2 w-16"></th>}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  Array.from({ length: 5 }).map((_, i) => (
                    <tr key={i}>
                      <td colSpan={isAdmin ? 10 : 7}>
                        <div className="skeleton h-6 w-full"></div>
                      </td>
                    </tr>
                  ))
                ) : sortedMovimientos.length === 0 ? (
                  <tr>
                    <td colSpan={isAdmin ? 10 : 7} className="text-center py-12">
                      <Receipt className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                      <p className="text-muted-foreground">No hay movimientos</p>
                      <p className="text-sm text-muted-foreground">
                        Carga un archivo Excel para comenzar
                      </p>
                    </td>
                  </tr>
                ) : (
                  sortedMovimientos.map((mov) => (
                    <tr key={mov.id} className={`hover:bg-accent/30 transition-colors ${selectedIds.has(mov.id) ? 'bg-primary/10' : ''}`}>
                      {isAdmin && (
                        <td className="px-2 py-1.5">
                          <input
                            type="checkbox"
                            className="rounded border-gray-300 cursor-pointer"
                            checked={selectedIds.has(mov.id)}
                            onChange={(e) => handleSelectOne(mov.id, e.target.checked)}
                            data-testid={`select-row-${mov.id}`}
                          />
                        </td>
                      )}
                      <td className="whitespace-nowrap px-2 py-1.5">{formatDate(mov.fecha)}</td>
                      <td className="max-w-[150px] truncate px-2 py-1.5" title={mov.concepto || '(Sin concepto)'}>
                        {mov.concepto || <span className="text-muted-foreground italic text-xs">(Sin concepto)</span>}
                      </td>
                      <td className="text-muted-foreground max-w-[100px] truncate px-2 py-1.5">
                        {mov.cliente_proveedor || '-'}
                      </td>
                      <td className="px-2 py-1.5">
                        <Badge className={`text-[10px] px-1.5 py-0.5 ${mov.tipo === 'ingreso' ? 'badge-ingreso' : mov.tipo === 'neutro' ? 'bg-gray-200 text-gray-600' : 'badge-egreso'}`}>
                          {mov.tipo === 'ingreso' ? 'Ing' : mov.tipo === 'neutro' ? '$0' : 'Egr'}
                        </Badge>
                      </td>
                      <td className="px-2 py-1.5">
                        {mov.tipo === 'egreso' ? (
                          <Select
                            value={mov.categoria_id || ''}
                            onValueChange={(v) => handleCategoryChange(mov.id, v)}
                          >
                            <SelectTrigger className="w-28 h-7 text-xs">
                              <SelectValue>
                                {mov.categoria_nombre ? (
                                  <div className="flex items-center gap-1">
                                    <div 
                                      className="w-2 h-2 rounded-full flex-shrink-0" 
                                      style={{ backgroundColor: categoriasList.find(c => c.id === mov.categoria_id)?.color }}
                                    />
                                    <span className="truncate text-xs">{mov.categoria_nombre}</span>
                                  </div>
                                ) : (
                                  <span className="text-muted-foreground text-xs">Sin cat.</span>
                                )}
                              </SelectValue>
                            </SelectTrigger>
                            <SelectContent position="popper" sideOffset={4}>
                              {categoriasList.map((cat) => (
                                <SelectItem key={cat.id} value={cat.id}>
                                  <div className="flex items-center gap-2">
                                    <div 
                                      className="w-2 h-2 rounded-full" 
                                      style={{ backgroundColor: cat.color }}
                                    />
                                    {cat.nombre}
                                  </div>
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </td>
                      <td className="px-2 py-1.5">
                        <Badge variant="outline" className="text-[10px] px-1.5 py-0.5">{mov.cuenta_nombre}</Badge>
                      </td>
                      <td className={`text-right font-medium px-2 py-1.5 ${mov.tipo === 'ingreso' ? 'text-emerald-600' : 'text-red-600'}`}>
                        {mov.tipo === 'ingreso' ? '+' : '-'}{formatCurrency(mov.monto)}
                      </td>
                      {isAdmin && (
                        <td className="px-1 py-1.5">
                          <div className="flex gap-0.5">
                            <Button 
                              variant="ghost" 
                              size="icon" 
                              className="h-6 w-6"
                              onClick={() => openEditDialog(mov)}
                              data-testid={`edit-mov-${mov.id}`}
                            >
                              <Edit2 className="w-3 h-3" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="icon" 
                              className="h-6 w-6 text-destructive"
                              onClick={() => handleDelete(mov.id)}
                              data-testid={`delete-mov-${mov.id}`}
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                        </td>
                      )}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          
          {/* Pagination Controls */}
          {totalCount > 0 && (
            <div className="flex flex-col sm:flex-row items-center justify-between px-4 py-3 border-t bg-muted/30 gap-3">
              <div className="flex items-center gap-2 sm:gap-4 w-full sm:w-auto justify-between sm:justify-start">
                <span className="text-xs sm:text-sm text-muted-foreground">
                  <span className="hidden sm:inline">Mostrando </span>
                  {((currentPage - 1) * pageSize) + 1}-{Math.min(currentPage * pageSize, totalCount)} 
                  <span className="hidden sm:inline"> de {totalCount.toLocaleString()} movimientos</span>
                  <span className="sm:hidden"> / {totalCount.toLocaleString()}</span>
                </span>
                <Select value={String(pageSize)} onValueChange={(v) => { setPageSize(Number(v)); setCurrentPage(1); }}>
                  <SelectTrigger className="w-16 sm:w-20 h-8 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="50">50</SelectItem>
                    <SelectItem value="100">100</SelectItem>
                    <SelectItem value="250">250</SelectItem>
                    <SelectItem value="500">500</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-1 sm:gap-2 w-full sm:w-auto justify-center">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(1)}
                  disabled={currentPage === 1}
                  className="hidden sm:inline-flex"
                >
                  Primera
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <span className="text-xs sm:text-sm px-2 sm:px-3 whitespace-nowrap">
                  {currentPage} / {Math.ceil(totalCount / pageSize)}
                </span>
                <Button
                  variant="outline"
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => setCurrentPage(p => Math.min(Math.ceil(totalCount / pageSize), p + 1))}
                  disabled={currentPage >= Math.ceil(totalCount / pageSize)}
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(Math.ceil(totalCount / pageSize))}
                  disabled={currentPage >= Math.ceil(totalCount / pageSize)}
                  className="hidden sm:inline-flex"
                >
                  Última
                </Button>
              </div>
            </div>
          )}
        </Card>

        {/* Edit Movement Dialog */}
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Editar Movimiento</DialogTitle>
            </DialogHeader>
            {editingMov && (
              <div className="space-y-4 py-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Fecha</Label>
                    <Input 
                      type="date" 
                      value={editingMov.fecha || ''} 
                      onChange={(e) => setEditingMov({...editingMov, fecha: e.target.value})}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Tipo</Label>
                    <Select 
                      value={editingMov.tipo} 
                      onValueChange={(v) => setEditingMov({...editingMov, tipo: v})}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="ingreso">Ingreso</SelectItem>
                        <SelectItem value="egreso">Egreso</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Concepto</Label>
                  <Input 
                    value={editingMov.concepto || ''} 
                    onChange={(e) => setEditingMov({...editingMov, concepto: e.target.value})}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Cliente/Proveedor</Label>
                  <Input 
                    value={editingMov.cliente_proveedor || ''} 
                    onChange={(e) => setEditingMov({...editingMov, cliente_proveedor: e.target.value})}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Monto</Label>
                    <Input 
                      type="number" 
                      value={editingMov.monto || ''} 
                      onChange={(e) => setEditingMov({...editingMov, monto: e.target.value})}
                    />
                  </div>
                  {editingMov.tipo === 'egreso' && (
                    <div className="space-y-2">
                      <Label>Categoría</Label>
                      <Select 
                        value={editingMov.categoria_id || ''} 
                        onValueChange={(v) => setEditingMov({...editingMov, categoria_id: v})}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Sin categoría" />
                        </SelectTrigger>
                        <SelectContent>
                          {categoriasList.map((cat) => (
                            <SelectItem key={cat.id} value={cat.id}>
                              <div className="flex items-center gap-2">
                                <div className="w-2 h-2 rounded-full" style={{ backgroundColor: cat.color }} />
                                {cat.nombre}
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditDialogOpen(false)}>Cancelar</Button>
              <Button onClick={handleSaveEdit} disabled={saving}>
                {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                <Save className="w-4 h-4 mr-2" />
                Guardar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
